import React, { useState } from "react";
import {
  Box,
  Typography,
  TextField,
  Select,
  MenuItem,
  Button,
  FormControl,
  FormHelperText,
} from "@mui/material";
import { useTranslation } from "react-i18next";
import { translateAgeString } from "../../../../../utils/exportUtils";
import "../../../../../components/components/css/InfoCard.css";


const AdmissionInfoCard = ({ title, data: initialData, editable, onSave }) => {
  const [isEditing, setIsEditing] = useState(false);
  const [data, setData] = useState(initialData);
  const [tempData, setTempData] = useState(initialData);
  const [loading, setLoading] = useState(false);
  const [errors, setErrors] = useState({}); 
  const { t, i18n } = useTranslation();
  const isAr = i18n.language === "ar";

  const handleEditClick = () => {
    setTempData([...data]);
    setIsEditing(true);
  };

  const admissionMapper = {
    "Name": "Name", 
    "BirthDate": "BirthDate",
    "Gender": "Gender",
    "Allergies": "Allergies",
    "Notes": "Notes",
    "Medications": "Medications",
    "Doctor Name": "DoctorName",
    "Doctor Phone No.": "DoctorPhone",
    "Street": "Street",
    "Appartment": "AppartmentHouse",
    "City": "City",
    "State": "State",
    "Zip Code": "ZipCode",
    // Parent fields
    "Email": "Email",
    "Phone": "Phone",
    "Address": "Address",
  };

  const getDifferences = () => {
    return tempData.reduce((acc, item, index) => {
      const { label, value } = item;
      const original = data[index];

      if (value !== original?.value) {
        if (label === "Name") {
          const nameParts = (value || "").trim().split(/\s+/);
          const firstName = nameParts[0] || "";
          const lastName = nameParts.slice(1).join(" ") || "";
          acc["FirstName"] = firstName;
          acc["LastName"] = lastName;
        } else {
          const apiKey = admissionMapper[label] || label;
          acc[apiKey] = value;
        }
      }

      return acc;
    }, {});
  };

  const handleSaveClick = async () => {
    try {
      setLoading(true);
      setErrors({}); 
      
      const diff = getDifferences();
      
      if (Object.keys(diff).length > 0) {
        if (onSave) {
          await onSave(diff);
        }
        setData([...tempData]);
      }
      
      setIsEditing(false);
    } catch (err) {
      console.error("Save error:", err);
      
      
      const apiErrors = err.response?.data || err.data || {};
      
      if (Object.keys(apiErrors).length > 0) {
        const fieldErrors = {};
        
        
        const reverseMapper = {};
        Object.entries(admissionMapper).forEach(([label, apiKey]) => {
          reverseMapper[apiKey] = label;
        });
        
        
        reverseMapper["FirstName"] = "Name";
        reverseMapper["LastName"] = "Name";
        
        Object.keys(apiErrors).forEach(key => {
          const label = reverseMapper[key] || key;
          const fieldIndex = tempData.findIndex(item => item.label === label);
          
          if (fieldIndex !== -1) {
            
            const errorMessage = Array.isArray(apiErrors[key]) 
              ? apiErrors[key][0] 
              : apiErrors[key];
            fieldErrors[fieldIndex] = errorMessage;
          }
        });
        
        setErrors(fieldErrors);
      } else {
        
        setErrors({ general: "Failed to save. Please try again." });
      }
    } finally {
      setLoading(false);
    }
  };

  const handleCancelClick = () => {
    setTempData([...data]);
    setIsEditing(false);
    setErrors({});
  };

  const handleInputChange = (index, value) => {
    const updated = [...tempData];
    updated[index] = { ...updated[index], value };
    setTempData(updated);
    
   
    if (errors[index]) {
      const newErrors = { ...errors };
      delete newErrors[index];
      setErrors(newErrors);
    }
  };

  return (
    <Box className="info-card">
      <Box className="info-card-header">
        <Typography variant="header">
          {t(`studentDetails.infoCard.titles.${title}`) || title}
        </Typography>
        {editable && !isEditing && (
          <Button
            onClick={handleEditClick}
            className="info-card-edit-btn"
            disabled={loading}
          >
            {t("actions.edit")}
          </Button>
        )}
        {isEditing && (
          <Box style={{ display: "flex", gap: 8 }}>
            <Button
              onClick={handleSaveClick}
              className="info-card-save-btn"
              disabled={loading}
            >
              {t("actions.save")}
            </Button>
            <Button
              onClick={handleCancelClick}
              className="info-card-cancel-btn"
              disabled={loading}
            >
              {t("actions.cancel")}
            </Button>
          </Box>
        )}
      </Box>

      <Box className="info-card-content">
        {tempData.map((item, idx) => (
          <Box key={idx} className="info-row">
            <Box className="info-label">
              {t(`studentDetails.infoCard.fields.${item.label}`) || item.label}
            </Box>
            {isEditing && item.editable ? (
              <>
                {item.type === "select" ? (
                  <FormControl fullWidth size="small" error={!!errors[idx]}>
<Select
  value={tempData[idx].value}
  onChange={(e) => handleInputChange(idx, e.target.value)}
  className="info-select"
>
  {item.options?.map((opt) => (
    <MenuItem key={opt.id} value={opt.id}>
      {item.label === "Country" 
        ? t(`countries.${opt.id}`, { defaultValue: opt.name }) 
        : item.label === "Nationality"
          ? (isAr ? opt.Name_ar : opt.name) 
          : ["Gender", "Status", "Meal Type"].includes(item.label)
            ? t(`options.${opt.name}`, { defaultValue: opt.name })
            : opt.name}
    </MenuItem>
  ))}
</Select>
                    {errors[idx] && (
                      <FormHelperText>{errors[idx]}</FormHelperText>
                    )}
                  </FormControl>
                ) : item.type === "date" ? (
                  <Box sx={{ width: '100%' }}>
                    <TextField
                      size="small"
                      type="date"
                      value={tempData[idx].value || ""}
                      onChange={(e) => handleInputChange(idx, e.target.value)}
                      fullWidth
                      className="info-text-field"
                      error={!!errors[idx]}
                      InputProps={{
                        style: { fontSize: 14 },
                      }}
                    />
                    {errors[idx] && (
                      <FormHelperText error>{errors[idx]}</FormHelperText>
                    )}
                  </Box>
                ) : (
                  <Box sx={{ width: '100%' }}>
                    <TextField
                      size="small"
                      value={tempData[idx].value || ""}
                      onChange={(e) => handleInputChange(idx, e.target.value)}
                      fullWidth
                      className="info-text-field"
                      error={!!errors[idx]}
                      InputProps={{
                        style: { fontSize: 14 },
                      }}
                    />
                    {errors[idx] && (
                      <FormHelperText error>{errors[idx]}</FormHelperText>
                    )}
                  </Box>
                )}
              </>
            ) : (
<Box className="info-value">
  {item.type === "multi-select"
    ? item.options
        ?.filter((opt) => item.value?.includes(opt.id))
        .map((opt) => {
          // لو الحقل هو Parents بنعرض الاسم زي ما هو، غير كدة بنترجم من options
          return item.label === "Parents" 
            ? opt.name 
            : t(`options.${opt.name}`, { defaultValue: opt.name });
        })
        .join(" - ") || "---"
    : item.type === "select"
      ? (() => {

          const currentVal = typeof item.value === 'object' ? item.value?.code : item.value;
          const selectedOpt = item.options?.find((opt) => opt.id === currentVal);

          // 3. ترجمة (الجنس، الحالة، نوع الوجبة، نوع الاتصال الطارئ)
          const labelsToTranslate = ["Gender", "Status", "Meal Type", "Emergency Type"];
          if (labelsToTranslate.includes(item.label)) {
            return selectedOpt ? t(`options.${selectedOpt.name}`, { defaultValue: selectedOpt.name }) : "---";
          }

          return selectedOpt ? selectedOpt.name : (item.value || "---");
        })()
      // 4. ترجمة العمر أو عرض القيمة العادية
      : (item.label === "Age" ? translateAgeString(item.value) : (item.value || "---"))
  }
</Box>
            )}
          </Box>
        ))}

        {errors.general && (
          <Typography variant="body2" color="error" sx={{ mt: 2 }}>
            {errors.general}
          </Typography>
        )}
      </Box>
    </Box>
  );
};

export default AdmissionInfoCard;